import proj4 from 'proj4';
import IMask from 'imask';

const coordsCenterFrance = {
	lat: 46.528_634_695_271_67,
	lng: 2.438_964_843_75,
};

/* globals google */

export const projections = [
	{
		id: 'WGS84',
		name: 'WGS 84',
		fields: ['lng', 'lat'],
		def: '+proj=longlat +datum=WGS84 +no_defs +type=crs',
	},
	{
		id: 'Lambert93',
		name: 'Lambert 93',
		fields: ['X', 'Y'],
		// eslint-disable-next-line max-len
		def: '+proj=lcc +lat_0=46.5 +lon_0=3 +lat_1=49 +lat_2=44 +x_0=700000 +y_0=6600000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
	},
];

projections.map(projection => proj4.defs(projection.id, projection.def));

const getMaskOptions = (/** @type {'lat' | 'lng'} */ axis) => ({
	mask:   'DD° MM\' SS" DIR',
	lazy:   false,
	blocks: {
		DD:  {
			mask:  Number,
			min:   0,
			max:   axis === 'lat' ? 90 : 180,
			scale: 0,
		},
		MM:  {
			mask:  Number,
			min:   0,
			max:   60,
			scale: 0,
		},
		SS:  {
			mask:  Number,
			min:   0,
			max:   60,
			scale: 5,
		},
		DIR: {
			mask: IMask.MaskedEnum,
			enum: axis === 'lat' ? [
				'N',
				'S',
			] : [
				'E',
				'O',
			],
		},
	},
});

export default class InputGeoController {
	/* @ngInject */
	/**
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {any} NgMap
	 * @param {any} User
	 */
	constructor(
		$scope,
		$timeout,
		$translate,
		NgMap,
		User,
	) {
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.NgMap = NgMap;

		/** @type {string | undefined} */
		this.customError = undefined;

		/** @type {any} */
		this.data = undefined;

		this.dataDMS = undefined;

		/** @type {boolean} */
		this.disabled = false;

		/** @type {boolean} */
		this.dms = false;

		/** @type {boolean} */
		this.showMap = false;

		/** @type {?google.maps.Map} */
		this.map = null;

		/** @type {string} */
		this.mapsKey = User.getAppConfig().GOOGLE.key;

		/** @type {?import('angular').INgModelController} */
		this.ngModelCtrl = null;

		this.options = {
			availableProjections:      projections.map(p => p.id),
			showCurrentPositionButton: true,
			showMap:                   false,
			showMapButton:             true,
			showProjectionSelection:   true,
		};

		this.projections = projections;

		this.required = false;

		/** @type {{lat: IMask.AllMaskedOptions, lng: IMask.AllMaskedOptions}} */
		this.maskOptions = {
			// @ts-ignore
			lat: getMaskOptions('lat'),
			// @ts-ignore
			lng: getMaskOptions('lng'),
		};
	}

	deleteValue() {
		this.data = undefined;
		this.ngModelCtrl?.$setViewValue(this.data);
		this.ngModelCtrl?.$validate();
	}

	hasData() {
		const projection = this.getCurrentProjection();
		return (
			projection === 'WGS84'
			&& this.data?.c?.lat
			&& this.data?.c?.lng
		) || (
			projection === 'Lambert93'
			&& this.data?.c?.X
			&& this.data?.c?.Y
		);
	}

	/**
	 * @returns {any}
	 */
	getAvailableProjections() {
		if (!this.options.availableProjections || this.options.availableProjections.length === 0) {
			return this.projections;
		}
		return this.projections.filter(
			(/** @type {any} */ projection) => this.options.availableProjections.includes(projection.id),
		);
	}

	getCurrentPosition() {
		navigator.geolocation.getCurrentPosition(position => {
			this.updateLocation({
				lat: position.coords.latitude,
				lng: position.coords.longitude,
			});
		});
	}

	getCurrentProjection() {
		return this.data?.p ?? this.projection;
	}

	/**
	 * @returns {undefined | {lat: number, lng: number}}
	 */
	getLocation() {
		const projection = this.getCurrentProjection();
		if (
			projection === 'WGS84'
			&& this.data?.c?.lat
			&& this.data?.c?.lng
		) {
			return this.data.c;
		}
		if (
			projection === 'Lambert93'
			&& this.data?.c?.X
			&& this.data?.c?.Y
		) {
			const values = proj4('Lambert93', 'WGS84', [
				this.data.c.X,
				this.data.c.Y,
			]);
			return {
				lat: values[1],
				lng: values[0],
			};
		}
		return undefined;
	}

	/**
	 * @param {string} projectionId
	 * @returns {string | undefined}
	 */
	getProjectionName(projectionId) {
		if (!projectionId) {
			// eslint-disable-next-line no-param-reassign
			projectionId = this.projection;
		}

		const projection = this.projections.find(
			(/** @type {any} */ p) => p.id === projectionId,
		);

		return projection?.name;
	}

	toDecimal(/** @type {string} */ value) {
		const matches = /^(?<d>\d{1,2})° (?<m>\d{1,2})' (?<s>[\d,.]+)\\?" (?<dir>[ENOS])$/.exec(value);
		let decimalValue = 0;
		if (matches?.groups) {
			const {
				d,
				m,
				s,
				dir,
			} = matches.groups;
			decimalValue = Number.parseInt(d, 10)
				+ (Number.parseInt(m, 10) / 60)
				+ (Number.parseFloat(s.replace(',', '.')) / 3600);
			if (dir === 'S' || dir === 'O') {
				decimalValue = -decimalValue;
			}
		}
		return decimalValue;
	}

	/**
	 * @param {number} value
	 * @param {'lat' | 'lng'} axis
	 * @returns {string}
	 */
	toDMS(value, axis) {
		const d = Math.abs(value);
		const rm = (d - Math.floor(d)) * 60;
		const rs = (rm - Math.floor(rm)) * 60;
		const direction = axis === 'lat' ? (value < 0 ? 'S' : 'N') : (value < 0 ? 'O' : 'E');
		return `${Math.floor(value)}° ${Math.floor(rm)}' ${rs.toFixed(5)}" ${direction}`;
	}

	toggleMap() {
		this.showMap = !this.showMap;
	}

	/**
	 * @param {{lat: number, lng: number}} c
	 */
	updateLocation(c) {
		if (this.projection === 'WGS84') {
			this.data = {
				p: this.projection,
				c: { ...c },
			};
			this.dataDMS = {
				lat: this.toDMS(c.lat, 'lat'),
				lng: this.toDMS(c.lng, 'lng'),
			};
		}
		if (this.projection === 'Lambert93') {
			const values = proj4('WGS84', 'Lambert93', [c.lng, c.lat]);
			this.data = {
				p: this.projection,
				c: {
					X: values[0],
					Y: values[1],
				},
			};
		}
	}

	/**
	 * @param {google.maps.event} event
	 */
	updateLocationFromMap(event) {
		if (!this.disabled) {
			this.updateLocation({
				// @ts-ignore
				lat: event.latLng.lat(),
				// @ts-ignore
				lng: event.latLng.lng(),
			});
		}
		this.ngModelCtrl?.$setDirty();
	}

	/**
	 * @param {?MouseEvent} event
	 * @param {any} projection
	 */
	updateProjection(event, projection) {
		event?.preventDefault();
		const fromProjection = this.projection;
		/** @type {number[]} */
		const coordinates = [];
		if (
			fromProjection === 'WGS84'
			&& this.data.c?.lng
			&& this.data.c?.lat
		) {
			coordinates.push(
				this.data.c.lng,
				this.data.c.lat,
			);
		}
		if (
			fromProjection === 'Lambert93'
			&& this.data.c?.X
			&& this.data.c?.Y
		) {
			coordinates.push(
				this.data.c.X,
				this.data.c.Y,
			);
		}
		const toProjection = projection.id;
		const c = {};
		if (coordinates.length === 2) {
			const values = proj4(fromProjection, toProjection, coordinates);

			/** @type {any} */
			if (values) {
				if (toProjection === 'WGS84') {
					c.lng = values[0];
					c.lat = values[1];
				}
				if (toProjection === 'Lambert93') {
					c.X = values[0];
					c.Y = values[1];
				}
			}
		}
		this.projection = projection.id;
		this.data = {
			p: projection.id,
			c,
		};
		this.ngModelCtrl?.$setViewValue(this.data);
		this.ngModelCtrl?.$validate();
	}

	/**
	 * @param {import('angular').IOnChangesObject} changes
	 */
	$onChanges(changes) {
		if (changes.projection) {
			this.updateProjection(null, this.projection);
		}
	}

	$onInit() {
		this.$scope.updateLocationFromMap = (/** @type {google.maps.event} */ event) => this.updateLocationFromMap(event);

		this.$scope.$watch('$ctrl.options.showMap', () => {
			this.showMap = this.options.showMap;
		});
		this.showMap = this.options.showMap;

		(async () => {
			this.map = await this.NgMap.getMap();

			let currentLocation = this.getLocation();
			if (!currentLocation) {
				currentLocation = coordsCenterFrance;
				this.map?.setZoom(5);
			}
			this.map?.setOptions({
				draggableCursor: 'crosshair',
			});
			this.initCenter = `${currentLocation.lat},${currentLocation.lng}`;

			// this.map.addListener('click', (event) => {
			// 	console.log('event', event);
			// });
			// const geocoder = new window.google.maps.Geocoder();
			// const countries = ct.getCountriesForTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
			// let bounds = new window.google.maps.LatLngBounds();
			// for (const country of countries) {
			// 	if (country.id === 'FR') {
			// 		country.id = 'FR';
			// 	}
			// 	try {
			// 		const geocoderResponse = await geocoder.geocode({ address: `country:${country.id}` });
			// 		for (const result of geocoderResponse.results) {
			// 			if (result.geometry.bounds) {
			// 				bounds = bounds.union(result.geometry.bounds);
			// 			}
			// 		}
			// 	}
			// 	catch { /* empty */ }
			// }
			// this.map.fitBounds(bounds);
		})();

		// default projection
		if (!this.projection) {
			this.projection = 'WGS84';
		}

		if (this.ngModelCtrl) {
			this.ngModelCtrl.$validators.locationRequired = () => {
				let valid = !this.required;
				if (this.required) {
					valid = this.hasData();
				}
				this.customError = !valid && !this.ngModelCtrl?.$pristine
					? this.$translate.instant('error.kform.locationRequired') : undefined;

				return valid;
			};
		}
	}
}
